import {css} from 'aphrodite';
import React from 'react';

import FolderDAO from 'file-browser/FolderDAO';
import FolderSelector from 'file-browser/FolderSelector';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {styles} from 'processes/settings/ProcessTaskSchemaEditor';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {SettingsLabel} from 'ui-library/SettingsField';

export const INACCESSIBLE_FOLDER_TEXT = 'No access';
export const ACCESSIBLE_FOLDER_TEXT = 'Choose folder';

// exported for test only
export async function areFoldersInaccessible(folderIds: Array<string | null>): Promise<Array<boolean>> {
  const folderMap = await FolderDAO.bulkFetch(
    folderIds.filter((id) => id != null).map((id) => id),
    {summary: '1'}
  );
  return folderIds.map((id) => (id == null ? false : folderMap[id] == null));
}

function RemoveFolderButton(props: {disabled: boolean; onClick: () => void}): JSX.Element {
  return (
    <Tooltip title="Remove folder">
      <Button
        size={ButtonSize.SMALL}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        icon="format-delete"
        className="u-mini-spacing-left"
        disabled={props.disabled}
        onClick={props.onClick}
      />
    </Tooltip>
  );
}

interface DefaultCreationFolderSelectorContainerProps {
  executionType: ProcessExecutionType;
  defaultCreationFolderId: string | null;
  isInaccessible: boolean;
  isDisabled?: boolean;
  onChange: (value: string | null) => void;
}

export function DefaultCreationFolderSelectorContainer(
  props: DefaultCreationFolderSelectorContainerProps
): JSX.Element {
  return (
    <>
      <SettingsLabel label="Default task group folder" name="Default task group folder" isRequired={false} />
      <div className="u-flex-display">
        <FolderSelector
          enableRedesign={true}
          showInventory={false}
          showNotebook={true}
          value={props.isInaccessible ? null : props.defaultCreationFolderId}
          noFolderText={props.isInaccessible ? INACCESSIBLE_FOLDER_TEXT : ACCESSIBLE_FOLDER_TEXT}
          readOnly={Boolean(props.isDisabled)}
          onChange={(e) => {
            props.onChange(e.target.value);
          }}
          unsetOnFetchFail={true}
        />
        <RemoveFolderButton
          disabled={props.defaultCreationFolderId == null}
          onClick={() => {
            props.onChange(null);
          }}
        />
      </div>
    </>
  );
}

interface DefaultEntryExecutionFolderSelectorContainerProps {
  executionType: ProcessExecutionType;
  defaultEntryExecutionFolderId: string | null;
  isInaccessible: boolean;
  isDisabled?: boolean;
  onChange: (value: string | null) => void;
}

export function DefaultEntryExecutionFolderSelectorContainer(
  props: DefaultEntryExecutionFolderSelectorContainerProps
): JSX.Element {
  return (
    <>
      <SettingsLabel
        label="Default execution entry folder"
        name="Default execution entry folder"
        isRequired={false}
      />
      {props.executionType === ProcessExecutionType.ENTRY ? (
        <div className="u-flex-display">
          <FolderSelector
            enableRedesign={true}
            showInventory={false}
            showNotebook={true}
            value={props.isInaccessible ? null : props.defaultEntryExecutionFolderId}
            noFolderText={props.isInaccessible ? INACCESSIBLE_FOLDER_TEXT : ACCESSIBLE_FOLDER_TEXT}
            onChange={(e) => {
              props.onChange(e.target.value);
            }}
            unsetOnFetchFail={true}
          />
          <RemoveFolderButton
            disabled={props.defaultEntryExecutionFolderId == null}
            onClick={() => {
              props.onChange(null);
            }}
          />
        </div>
      ) : (
        <span className={css(styles.descriptionText)}>
          This setting is only available if execution type is Entry.
        </span>
      )}
    </>
  );
}
